/**
 * v136.0 — Sign-In CTA, local to the Range Log header.
 *
 * Anonymous Forge usage remains the friction-free default. Per Sal's decision
 * (B) the Sign-In *prompt* lives where the value is: the Range Log header.
 *
 * UI states:
 *   - Anonymous: ember Sign-In button + "Sync your training data" subtext
 *   - Signed in: cyan "Signed in as <email>" chip with cloud-status indicator
 *   - Loading: skeleton dot
 *
 * On sign-in success (return from /account/login with ?signedin=1) the
 * IdentityProvider re-fetches and re-renders this surface as the signed-in
 * variant. A welcome toast fires elsewhere.
 */

import { CloudUpload, LogIn, CheckCircle2, Loader2 } from 'lucide-react';
import { useIdentity } from './useIdentity';
import { getSignInUrl } from './identityBridge';

interface SignInCTAProps {
  /** Optional return URL — defaults to the Range Log. */
  returnPath?: string;
  /** When true, render a compact chip variant. */
  compact?: boolean;
}

export function SignInCTA({ returnPath, compact }: SignInCTAProps) {
  const { identity, loading } = useIdentity();

  if (loading) {
    return (
      <div className="inline-flex items-center gap-2 px-3 py-1.5 text-xs text-zinc-500">
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        <span>Checking sign-in…</span>
      </div>
    );
  }

  if (identity.signedIn && identity.customerEmail) {
    return (
      <div
        className={
          compact
            ? 'inline-flex items-center gap-1.5 px-2 py-1 border border-cyan-700/40 bg-cyan-950/20 rounded text-[11px] text-cyan-300'
            : 'inline-flex items-center gap-2 px-3 py-1.5 border border-cyan-700/40 bg-cyan-950/20 rounded text-xs text-cyan-300'
        }
        title="Cloud sync active"
      >
        <CheckCircle2 className="w-3.5 h-3.5" />
        <span className="truncate max-w-[12rem]">
          {compact ? 'Signed in' : `Signed in · ${identity.customerEmail}`}
        </span>
      </div>
    );
  }

  // Anonymous — Sign-In CTA
  return (
    <a
      href={getSignInUrl(returnPath)}
      // Open at the top-level (storefront), not inside the iframe — the
      // Shopify customer login flow needs the storefront context.
      target="_top"
      rel="noopener"
      className={
        compact
          ? 'inline-flex items-center gap-1.5 px-2 py-1 border border-ember-500/50 text-ember-300 rounded text-[11px] hover:bg-ember-500/10'
          : 'inline-flex items-center gap-2 px-3 py-2 border border-ember-500/50 text-ember-300 rounded-md text-sm hover:bg-ember-500/10 transition'
      }
    >
      <LogIn className="w-3.5 h-3.5" />
      <span>
        {compact ? (
          'Sign in'
        ) : (
          <>
            Sign in · <span className="text-zinc-400">sync your training data</span>
          </>
        )}
      </span>
      {!compact && <CloudUpload className="w-3.5 h-3.5 opacity-60 ml-1" />}
    </a>
  );
}
